import { Save, FileDown, Loader2, Mail, Eye } from "lucide-react";
import { IssueButton } from "@/components/quote/IssueControls";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { eur, quoteTotals, marginTone } from "@/lib/pricing";
import { STATUS, EXTRA_CLAUSES } from "@/lib/constants";
import { Switch } from "@/components/ui/switch";
import { PaymentInput } from "@/components/PaymentInput";

const Row = ({ label, value, testId, strong }) => (
  <div className={`flex justify-between items-baseline ${strong ? "text-lg" : "text-sm"}`}>
    <span className={strong ? "font-semibold" : "text-slate-400"}>{label}</span>
    <span className={`font-mono ${strong ? "text-amber-400 font-semibold text-2xl" : ""}`} data-testid={testId}>{value}</span>
  </div>
);

const TONES = {
  red: "border-red-500/50 bg-red-500/10 text-red-300",
  yellow: "border-yellow-500/50 bg-yellow-500/10 text-yellow-300",
  green: "border-emerald-500/50 bg-emerald-500/10 text-emerald-300",
};

const MarginBox = ({ t }) => {
  const tone = marginTone(t.marginePct);
  return (
    <div className={`rounded-md border p-3 space-y-1.5 ${TONES[tone]}`} data-testid="quote-margin-box" data-tone={tone}>
      <div className="flex justify-between text-sm"><span className="text-slate-300">Somma costi</span><span className="font-mono" data-testid="quote-total-costs">{eur(t.costi)}</span></div>
      <div className="flex justify-between items-baseline">
        <span className="font-semibold text-sm">Marginalità</span>
        <span className="font-mono font-semibold"><span data-testid="quote-margin-amount">{eur(t.margine)}</span> · <span data-testid="quote-margin-pct">{t.marginePct.toLocaleString("it-IT", { maximumFractionDigits: 1 })}%</span></span>
      </div>
    </div>
  );
};

export const SummaryPanel = ({ q, update, saving, onSave, onPdf, onIssue, onStatus, locked, onMail, mailing, paymentOptions }) => {
  const t = quoteTotals(q);
  return (
    <aside className="xl:sticky xl:top-20 space-y-4" data-testid="summary-panel">
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-4">
        <p className="micro-label">Riepilogo (uso interno)</p>
        <Row label="Somma servizi" value={eur(t.somma)} testId="quote-total-somma" />
        {t.trasporto > 0 && <p className="text-xs text-slate-500 -mt-3 text-right" data-testid="quote-total-transport">di cui spese di viaggio {eur(t.trasporto)}</p>}
        {t.pasti > 0 && <p className="text-xs text-slate-500 -mt-3 text-right" data-testid="quote-total-meals">di cui pasti {eur(t.pasti)}</p>}
        <fieldset disabled={locked} className="space-y-4 disabled:opacity-80">
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm text-slate-400">1° sconto su materiale %</Label>
            <span className="font-mono text-xs text-slate-500">su {eur(t.materiale)}</span>
          </div>
          <div className="flex items-center gap-3">
            <Input type="number" min={0} max={100} step="any" value={q.material_discount ?? 0} onChange={(e) => update({ material_discount: Math.min(100, +e.target.value || 0) })} className="h-8 font-mono w-28" data-testid="quote-material-discount-input" />
            <span className="ml-auto font-mono text-sm text-red-300" data-testid="quote-material-discount-amount">- {eur(t.scontoMat)}</span>
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between gap-2">
            <Label className="text-sm text-slate-400">2° sconto sul totale</Label>
            <ToggleGroup type="single" size="sm" value={q.discount_type} onValueChange={(v) => v && update({ discount_type: v })}>
              <ToggleGroupItem value="percent" className="h-7 px-2 font-mono text-xs" data-testid="discount-type-percent">%</ToggleGroupItem>
              <ToggleGroupItem value="amount" className="h-7 px-2 font-mono text-xs" data-testid="discount-type-amount">€</ToggleGroupItem>
            </ToggleGroup>
          </div>
          <div className="flex items-center gap-3">
            <Input type="number" min={0} step="any" value={q.discount_value} onChange={(e) => update({ discount_value: +e.target.value || 0 })} className="h-8 font-mono w-28" data-testid="quote-discount-input" />
            <span className="ml-auto font-mono text-sm text-red-300" data-testid="quote-discount-amount">- {eur(t.sconto)}</span>
          </div>
        </div>
        </fieldset>
        <div className="border-t border-slate-800 pt-4">
          <Row label="Totale servizio" value={eur(t.totale)} testId="quote-grand-total" strong />
          <p className="text-xs text-slate-500 mt-1">IVA esclusa. Nel PDF il cliente vede solo questi totali.</p>
        </div>
        <MarginBox t={t} />
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="micro-label">Stato</Label>
            <Select value={q.status} onValueChange={onStatus} disabled={!locked}>
              <SelectTrigger className="h-8" data-testid="quote-status-select"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(STATUS).filter(([k]) => k !== "bozza" || !locked).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="micro-label">Revisione</Label>
            <p className="h-8 flex items-center font-mono text-sm" data-testid="quote-revision-value">{q.revision || "—"}</p>
          </div>
        </div>
        <div className="space-y-2 pt-1">
          {!locked && <IssueButton q={q} disabled={saving} onIssue={onIssue} />}
          <div className="grid grid-cols-2 gap-2">
            {!locked && <Button onClick={onSave} disabled={saving} className="bg-amber-400 hover:bg-amber-300 text-slate-900" data-testid="btn-save-quote">
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}Salva preventivo
            </Button>}
            <Button variant="outline" onClick={() => onPdf(false)} disabled={saving} className={locked ? "col-span-2" : ""} data-testid="btn-export-pdf"><Eye className="h-4 w-4 mr-2" />PDF</Button>
          </div>
          <Button variant="ghost" className="w-full border border-dashed border-slate-700" onClick={() => onPdf(true, true)} disabled={saving} data-testid="btn-export-pdf-plain"><FileDown className="h-4 w-4 mr-2" />Copia senza prezzi e clausole</Button>
          <Button variant="ghost" className="w-full border border-dashed border-slate-700" onClick={onMail} disabled={saving || mailing} data-testid="btn-email-material-list">{mailing ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Mail className="h-4 w-4 mr-2" />}Invia lista materiale via email</Button>
        </div>
      </div>
      <fieldset disabled={locked} className="space-y-4">
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-2">
        <p className="micro-label">Modalità di pagamento (nel PDF)</p>
        <div className="flex flex-wrap items-center gap-2">
          <Label className="micro-label mr-auto">Acconto</Label>
          <Input type="number" min={0} max={100} value={q.deposit_pct ?? 0} onChange={(e) => update({ deposit_pct: e.target.value === "" ? "" : Math.min(Math.max(+e.target.value || 0, 0), 100) })} className="h-8 w-16 font-mono" aria-label="Percentuale acconto" data-testid="quote-deposit-pct-input" /><span className="text-sm text-slate-400">%</span>
          <Input value={q.deposit_method ?? ""} onChange={(e) => update({ deposit_method: e.target.value })} placeholder="Bonifico immediato" className="h-8 w-40" aria-label="Modalità acconto" data-testid="quote-deposit-method-input" />
        </div>
        <p className="text-xs text-slate-500 text-right" data-testid="quote-deposit-amount">{+q.deposit_pct > 0 ? `${eur(t.totale * q.deposit_pct / 100)} alla conferma` : "Nessun acconto"}</p>
        <Label className="micro-label">Saldo</Label>
        <PaymentInput value={q.payment_terms} onChange={(v) => update({ payment_terms: v })} options={paymentOptions} id="quote-payment-options" testId="quote-payment-input" />
        <div className="flex items-center gap-3 pt-2">
          <Label className="micro-label">Validità offerta (giorni)</Label>
          <Input type="number" min={1} value={q.validity_days ?? 1} onChange={(e) => update({ validity_days: Math.max(1, parseInt(e.target.value || "1", 10)) })} className="h-8 w-20 font-mono ml-auto" data-testid="quote-validity-input" />
        </div>
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-2">
        <Label className="micro-label">Note ed esclusioni (nel PDF)</Label>
        <Textarea rows={6} value={q.notes} onChange={(e) => update({ notes: e.target.value })} className="text-xs" data-testid="quote-notes-input" />
      </div>
      <div className="bg-slate-900 border border-slate-800 rounded-lg p-5 space-y-2" data-testid="quote-extra-clauses">
        <Label className="micro-label">Clausole aggiuntive (nel PDF)</Label>
        {EXTRA_CLAUSES.map(([k, label]) => {
          const on = (q.clauses_on || []).includes(k);
          return (
            <label key={k} className="flex items-center gap-3 text-sm cursor-pointer">
              <Switch checked={on} onCheckedChange={(v) => update({ clauses_on: v ? [...(q.clauses_on || []), k] : (q.clauses_on || []).filter((x) => x !== k) })} data-testid={`quote-clause-${k}-toggle`} />
              <span className={on ? "" : "text-slate-400"}>{label}</span>
            </label>
          );
        })}
        <p className="text-xs text-slate-500">I testi si modificano in Impostazioni.</p>
      </div>
      </fieldset>
    </aside>
  );
};
